import React from 'react';

import { useSortable } from '@dnd-kit/sortable';
import { flexRender, Header } from '@tanstack/react-table';
import clsx from 'clsx';

import { SortablePill } from '../Pill';
import { WithSubData } from './DataDisplayTable';
import { Alignment, ColumnMeta, getCommonPinningStyles, isColumnMeta } from './utils';

interface RenderHeaderComponentProps<T> {
  header: Header<WithSubData<T>, unknown>;
  width: number;
  isResizing: string;
  isDraggable?: boolean;
  canResize: boolean;
  resizeCfg?: string[];
  setIsResizing: React.Dispatch<React.SetStateAction<string>>;
  thClasses?: string;
  extraClasses?: string;
  pillBtnVariant?: 'primary' | 'blue' | 'muted' | 'muted_light';
  supportsLightTheme?: boolean;
}

export const RenderHeaderComponent = <T,>({
  header,
  width,
  isResizing,
  canResize,
  setIsResizing,
  resizeCfg = [],
  isDraggable = false,
  thClasses = '',
  pillBtnVariant = 'blue',
  extraClasses = '',
  supportsLightTheme = false,
}: RenderHeaderComponentProps<T>) => {
  const column = header.column;
  const columnDefMeta = column.columnDef.meta;
  const columnHeaderClassName = header.isPlaceholder
    ? undefined
    : (columnDefMeta as ColumnMeta | undefined)?.headerClassName;
  const hidesPill =
    columnDefMeta != null && 'headerNoPill' in columnDefMeta && columnDefMeta.headerNoPill;
  const { alignment, sortable } = (() => {
    if (isColumnMeta(columnDefMeta)) {
      const alignment = columnDefMeta.alignment || 'left';
      const sortable = column.getCanSort();
      return { alignment, sortable };
    }
    return { alignment: Alignment.left, sortable: false };
  })();

  const sortValue = sortable
    ? (column.getIsSorted() || column.getNextSortingOrder()) === 'desc'
      ? 'desc'
      : 'asc'
    : '';

  const handleDragStart = () => {
    setIsResizing(header.column.id);
    document.addEventListener('mouseup', handleDragEnd);
    document.addEventListener('touchend', handleDragEnd);
  };

  const handleDragEnd = () => {
    setIsResizing('');
    document.removeEventListener('mouseup', handleDragEnd);
    document.removeEventListener('touchend', handleDragEnd);
  };

  const { isDragging, transform, transition } = useSortable({
    id: header.id,
  });

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        transition,
      }
    : undefined;

  const thClassNames = `relative group text-${alignment} ${isDragging ? 'bg-gray-700' : 'bg-background-canvas'}  cursor-${sortable ? 'pointer' : 'default'} py-[7px] pr-4 pl-2  ${isResizing === header.id ? 'border-r border-r-red-400' : 'border-gray-950'} ${supportsLightTheme ? 'light:bg-white' : ''} ${thClasses} ${extraClasses}`;
  return (
    <th
      key={header.id}
      className={clsx(thClassNames, columnHeaderClassName)}
      colSpan={header.colSpan > 1 ? header.colSpan : undefined}
      style={{
        position: 'relative',
        width: width || header.getSize(),
        ...style,
        ...getCommonPinningStyles(column, 0, true, isDragging),
      }}
    >
      {header.isPlaceholder ? null : !hidesPill ? (
        <div
          style={{
            display: 'flex',
            justifyContent:
              alignment === 'left' ? 'flex-start' : alignment === 'right' ? 'flex-end' : 'center',
          }}
          onClick={sortable ? column.getToggleSortingHandler() : undefined}
        >
          <SortablePill
            variant={pillBtnVariant}
            useTransform={false}
            id={header.id}
            label={column.columnDef.header as string}
            isDraggable={isDraggable}
            sortValue={sortValue}
            isSorted={!!column.getIsSorted()}
            className={
              supportsLightTheme
                ? 'light:border-gray-300 light:bg-gray-100 light:text-gray-700'
                : undefined
            }
          />
        </div>
      ) : (
        flexRender(column.columnDef.header, header.getContext())
      )}
      {!resizeCfg.includes(header.id) && canResize && (
        <div
          className="absolute right-0 top-0 h-full w-[7px] cursor-col-resize touch-none select-none border-r-[1px] border-dashed border-red-400 opacity-0 transition-opacity group-hover:opacity-100"
          onMouseDown={(e) => {
            header.getResizeHandler()(e);
            handleDragStart();
          }}
          onTouchStart={(e) => {
            header.getResizeHandler()(e);
            handleDragStart();
          }}
        />
      )}
    </th>
  );
};

export const RenderHeader = React.memo(RenderHeaderComponent) as typeof RenderHeaderComponent;
